import React, { useCallback, useEffect, useState } from 'react';
import { Flame, Scale, ArrowRightLeft, RefreshCw, CheckCircle2 } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { SkeletonRows } from './OrgPanelShared';

const RISK_STYLES = {
  low: 'bg-emerald-50 text-emerald-600',
  moderate: 'bg-amber-50 text-amber-600',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

export default function WorkforceBalanceTab() {
  const [workload, setWorkload] = useState(null);
  const [fairness, setFairness] = useState(null);
  const [transfers, setTransfers] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const [w, f, t] = await Promise.all([
        apiCall('/api/org/ai/workload'),
        apiCall('/api/org/ai/fairness'),
        apiCall('/api/org/ai/transfer-recommendations'),
      ]);
      setWorkload(w); setFairness(f); setTransfers(t.recommendations || []);
    } catch (e) { toast.error(e.message); setWorkload({}); setFairness({}); setTransfers([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const assignTransfer = async (jobId, workerId, name) => {
    setBusy(`${jobId}-${workerId}`);
    try {
      await apiCall(`/api/org/shifts/${jobId}/assign`, {
        method: 'POST', body: JSON.stringify({ worker_id: workerId, force: true }),
      });
      toast.success(`${name} transferred and assigned`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  if (workload === null || fairness === null) return <SkeletonRows n={5} />;
  const wl = workload.workers || [];
  const fr = fairness.workers || [];
  return (
    <div className="space-y-5" data-testid="workforce-balance-tab">
      {/* Burnout risk */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1.5">
            <Flame className="w-4 h-4 text-orange-500" /> Burnout Risk (last 30 days)
          </p>
          <span className="text-xs text-slate-500">
            {workload.at_risk_count ? (
              <span className="text-orange-600 font-semibold" data-testid="workload-at-risk-count">{workload.at_risk_count} worker(s) at risk</span>
            ) : 'All healthy'}
            <button onClick={load} className="ml-3 text-indigo-600" data-testid="balance-refresh"><RefreshCw className="w-3.5 h-3.5 inline" /></button>
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-400">
              <tr><th className="text-left py-1.5 font-medium">Worker</th><th className="text-right font-medium">Wk hrs</th><th className="text-right font-medium">Mo hrs</th><th className="text-right font-medium">Consec days</th><th className="text-right font-medium">OT hrs</th><th className="text-right font-medium">Rest issues</th><th className="text-right font-medium">Risk</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {wl.slice(0, 25).map((w) => (
                <tr key={w.worker_id} data-testid={`workload-row-${w.worker_id}`}>
                  <td className="py-2 text-slate-800 font-medium">{w.name}</td>
                  <td className="text-right text-slate-600">{w.weekly_hours}</td>
                  <td className="text-right text-slate-600">{w.monthly_hours}</td>
                  <td className="text-right text-slate-600">{w.consecutive_days}</td>
                  <td className={`text-right ${w.overtime_hours ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>{w.overtime_hours}</td>
                  <td className={`text-right ${w.rest_violations ? 'text-amber-600 font-semibold' : 'text-slate-400'}`}>{w.rest_violations}</td>
                  <td className="text-right">
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${RISK_STYLES[w.risk_band]}`}>{w.burnout_risk} · {w.risk_band}</span>
                  </td>
                </tr>
              ))}
              {!wl.length && <tr><td colSpan={7} className="py-4 text-center text-slate-400">No roster workers.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fairness */}
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="fairness-section">
        <p className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1.5 mb-1">
          <Scale className="w-4 h-4 text-indigo-500" /> Opportunity Fairness (30 days)
        </p>
        <p className="text-[11px] text-slate-400 mb-3">
          {fairness.fairness_enabled ? 'Fairness boost is ON — under-utilized workers get up to +8 match points.' : 'Fairness boost is OFF (see AI Policy).'}
          {' '}Even share would be {fairness.avg_share_pct}% each · {fairness.overloaded_count} overloaded · {fairness.idle_count} with zero assignments.
        </p>
        <div className="space-y-1.5">
          {fr.slice(0, 25).map((w) => (
            <div key={w.worker_id} className="flex items-center gap-2" data-testid={`fairness-row-${w.worker_id}`}>
              <span className="text-xs text-slate-700 w-40 truncate">{w.name}</span>
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full ${w.share_pct > fairness.avg_share_pct * 2 ? 'bg-orange-500' : 'bg-indigo-500'}`}
                  style={{ width: `${Math.min(100, w.share_pct * 2)}%` }} />
              </div>
              <span className="text-[11px] text-slate-500 w-44 text-right">
                {w.share_pct}% · {w.hours_30d}h · {w.weekend_shifts}we/{w.night_shifts}nt/{w.premium_shifts}pr
              </span>
            </div>
          ))}
          {!fr.length && <p className="text-xs text-slate-400">No assignment history yet.</p>}
        </div>
      </div>

      {/* Transfers */}
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="transfers-section">
        <p className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1.5 mb-3">
          <ArrowRightLeft className="w-4 h-4 text-violet-500" /> Transfer Recommendations
        </p>
        {transfers === null ? <SkeletonRows n={2} /> : !transfers.length ? (
          <p className="text-xs text-slate-400">No understaffed locations need transfers right now.</p>
        ) : transfers.map((r) => (
          <div key={r.job_id} className="border border-violet-200 bg-violet-50/50 rounded-xl p-3 mb-2" data-testid={`transfer-rec-${r.job_id}`}>
            <p className="text-sm font-semibold text-slate-900">
              {r.to_location} needs cover — '{r.job_title}' ({r.start_time?.replace('T', ' ')})
              <span className="ml-2 text-[11px] text-violet-600 font-medium">{r.unfilled_count} unfilled here</span>
            </p>
            <div className="mt-2 space-y-1.5">
              {r.candidates.map((c) => (
                <div key={c.worker_id} className="flex items-center justify-between gap-2 bg-white rounded-lg px-3 py-2" data-testid={`transfer-candidate-${r.job_id}-${c.worker_id}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-800">{c.name} <span className="text-slate-400">from {c.from_location}</span></p>
                    <p className="text-[11px] text-slate-500 truncate">{c.factors.join(' · ')}</p>
                  </div>
                  <button onClick={() => assignTransfer(r.job_id, c.worker_id, c.name)}
                    disabled={busy === `${r.job_id}-${c.worker_id}`}
                    className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-50"
                    data-testid={`transfer-assign-${r.job_id}-${c.worker_id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Transfer
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
